/**
 * Redesigned Admin Dashboard
 * Gaming/Tech aesthetic with Phoenix Orange theme
 */

import { useEffect, useState } from 'react';
import {
  DollarSign,
  Users,
  ShoppingCart,
  TrendingUp,
  Server,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { ParticleBackground } from '../../components/effects/ParticleBackground';
import { StatCard, StatGrid, StatCardSkeleton } from '../../components/ui/StatCard';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.new';
import { Button } from '../../components/ui/Button.new';

interface DashboardStats {
  totalRevenue: number;
  revenueChange: number;
  totalClients: number;
  clientsChange: number;
  activeServices: number;
  servicesChange: number;
  pendingOrders: number;
  ordersChange: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch dashboard data
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/v1/admin/dashboard');
      // const data = await response.json();

      // Mock data for demonstration
      setTimeout(() => {
        setStats({
          totalRevenue: 12450.75,
          revenueChange: 12.5,
          totalClients: 248,
          clientsChange: 8.2,
          activeServices: 421,
          servicesChange: 5.7,
          pendingOrders: 12,
          ordersChange: -3.1,
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-8 space-y-8">
      <ParticleBackground />

      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-4xl font-bold text-white mb-2 font-display">
          Welcome back, <span className="text-orange-500">Admin</span>
        </h1>
        <p className="text-slate-400">
          Here's what's happening with your business today
        </p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <StatGrid columns={4}>
          {[1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} />
          ))}
        </StatGrid>
      ) : stats ? (
        <StatGrid columns={4}>
          <StatCard
            title="Total Revenue"
            value={`$${stats.totalRevenue.toLocaleString()}`}
            icon={DollarSign}
            color="orange"
            trend={{
              value: stats.revenueChange,
              label: 'from last month',
              isPositive: stats.revenueChange > 0,
            }}
            glow
          />

          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={Users}
            color="purple"
            trend={{
              value: stats.clientsChange,
              label: 'from last month',
              isPositive: stats.clientsChange > 0,
            }}
            glow
          />

          <StatCard
            title="Active Services"
            value={stats.activeServices}
            icon={Server}
            color="cyan"
            trend={{
              value: stats.servicesChange,
              label: 'from last month',
              isPositive: stats.servicesChange > 0,
            }}
            glow
          />

          <StatCard
            title="Pending Orders"
            value={stats.pendingOrders}
            icon={ShoppingCart}
            color="green"
            trend={{
              value: Math.abs(stats.ordersChange),
              label: 'from last month',
              isPositive: stats.ordersChange > 0,
            }}
            glow
          />
        </StatGrid>
      ) : null}

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card variant="glass" className="animate-slide-up">
          <CardHeader>
            <CardTitle>Revenue Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-slate-400">
              {/* TODO: Integrate Chart.js component */}
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 text-orange-500" />
                <p>Revenue chart will be displayed here</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card variant="glass" className="animate-slide-up" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <ActivityItem
                icon={<CheckCircle className="w-5 h-5 text-green-500" />}
                title="New order placed"
                description="Order #1234 by John Doe"
                time="2 minutes ago"
              />
              <ActivityItem
                icon={<Users className="w-5 h-5 text-purple-500" />}
                title="New client registered"
                description="jane.smith@example.com"
                time="15 minutes ago"
              />
              <ActivityItem
                icon={<Server className="w-5 h-5 text-cyan-500" />}
                title="Service activated"
                description="VPS Pro Plan for acme-corp.com"
                time="1 hour ago"
              />
              <ActivityItem
                icon={<AlertCircle className="w-5 h-5 text-orange-500" />}
                title="Payment pending"
                description="Invoice #INV-2024-001"
                time="2 hours ago"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card variant="glow" className="animate-slide-up" style={{ animationDelay: '200ms' }}>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickActionButton
              icon={<Users className="w-6 h-6" />}
              label="New Client"
              onClick={() => {/* TODO: Navigate to create client */}}
            />
            <QuickActionButton
              icon={<ShoppingCart className="w-6 h-6" />}
              label="New Order"
              onClick={() => {/* TODO: Navigate to create order */}}
            />
            <QuickActionButton
              icon={<DollarSign className="w-6 h-6" />}
              label="New Invoice"
              onClick={() => {/* TODO: Navigate to create invoice */}}
            />
            <QuickActionButton
              icon={<Server className="w-6 h-6" />}
              label="Manage Services"
              onClick={() => {/* TODO: Navigate to services */}}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Activity Item Component
interface ActivityItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  time: string;
}

function ActivityItem({ icon, title, description, time }: ActivityItemProps) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-700/30 transition-colors">
      <div className="flex-shrink-0 mt-1">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-sm text-slate-400 truncate">{description}</p>
      </div>
      <div className="flex-shrink-0">
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {time}
        </span>
      </div>
    </div>
  );
}

// Quick Action Button Component
interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function QuickActionButton({ icon, label, onClick }: QuickActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-lg bg-slate-700/30 border border-slate-600 hover:border-orange-500/50 hover:bg-slate-700/50 transition-all duration-300 hover:-translate-y-1 group"
    >
      <div className="text-slate-400 group-hover:text-orange-500 transition-colors">
        {icon}
      </div>
      <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
        {label}
      </span>
    </button>
  );
}
